import { sortRecordsNewestFirst } from '../../utils/analyticsShared';
import { collection, getDocs, limit, query, startAfter, where } from 'firebase/firestore';
import { normalizeMinimumStock } from '../../utils/adminPanelHelpers';
import { normalizeCanonicalEvent } from '../../utils/canonicalEvent';
import { getAnalyticsCollectionPath, prepareAnalyticsMeldingen } from '../../utils/analyticsMeldingLinkage';
import { IDENTITY_STATUS, resolveRobotRecord } from '../../utils/identityResolver';
import analyticsService from '../../utils/analyticsService';
import { buildAnalyticsCountContract } from '../analytics/analyticsCountContract';
import { countUnreadHumanMeldingen } from '../../utils/meldingBadge';

const textValue = (value) => String(value ?? '').trim();

async function mapWithConcurrency(items, concurrency, mapper) {
  const results = new Array(items.length);
  let nextIndex = 0;
  const workerCount = Math.min(Math.max(1, concurrency), items.length);
  await Promise.all(Array.from({ length: workerCount }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index], index);
    }
  }));
  return results;
}

function firstTextValue(values = []) {
  return values.map(textValue).find(Boolean) || '';
}

function uniqueTextValues(values = []) {
  return [...new Set(values.map(textValue).filter(Boolean))];
}

function normalizeAnalyticsSourceRecord(docSnap, {
  source,
  branchId,
  automatic = false,
  identityHint = '',
} = {}) {
  const data = docSnap.data() || {};
  const selectedRobot = data.selectedRobot && typeof data.selectedRobot === 'object' ? data.selectedRobot : {};
  const nestedRobot = data.robot && typeof data.robot === 'object' ? data.robot : {};
  const selectedNestedRobot = selectedRobot.robot && typeof selectedRobot.robot === 'object'
    ? selectedRobot.robot
    : {};
  const documentIdAliases = uniqueTextValues([
    data.robotDocumentId, data.robotDocId, data.documentId,
    nestedRobot.documentId, nestedRobot.docId,
    selectedRobot.robotDocumentId, selectedRobot.robotDocId,
    selectedNestedRobot.documentId, selectedNestedRobot.docId,
  ]);
  const robotIdAliases = uniqueTextValues([
    data.robotId, data.robotID, data.sourceRobotId,
    nestedRobot.robotId, nestedRobot.id,
    selectedRobot.robotId, selectedRobot.id,
    selectedNestedRobot.robotId, selectedNestedRobot.id,
  ]);
  const serialAliases = uniqueTextValues([
    data.serialNumber, data.serial, data.robotSerial, data.belroboticsId, data.belroboticsID,
    nestedRobot.serialNumber, nestedRobot.serial, nestedRobot.robotSerial,
    selectedNestedRobot.serialNumber, selectedNestedRobot.serial,
    identityHint,
    ['myRobotLiveStatus', 'robotHealthSummary'].includes(source) ? docSnap.id : '',
  ]);
  const robotDocumentId = firstTextValue([
    data.robotDocumentId,
    data.robotDocId,
    data.documentId,
    nestedRobot.documentId,
    nestedRobot.docId,
    selectedRobot.robotDocumentId,
    selectedRobot.robotDocId,
    selectedNestedRobot.documentId,
    selectedNestedRobot.docId,
  ]);
  const robotId = firstTextValue([
    data.robotId,
    data.robotID,
    data.sourceRobotId,
    nestedRobot.robotId,
    nestedRobot.id,
    selectedRobot.robotId,
    selectedRobot.id,
    selectedNestedRobot.robotId,
    selectedNestedRobot.id,
  ]);
  const serialNumber = firstTextValue([
    data.serialNumber,
    data.serial,
    data.robotSerial,
    data.belroboticsId,
    data.belroboticsID,
    nestedRobot.serialNumber,
    nestedRobot.serial,
    nestedRobot.robotSerial,
    selectedNestedRobot.serialNumber,
    selectedNestedRobot.serial,
    identityHint,
    ['myRobotLiveStatus', 'robotHealthSummary'].includes(source) ? docSnap.id : '',
  ]);
  const effectiveSerial = serialNumber || (/^BR[A-Z0-9]{6,}$/i.test(robotId) ? robotId : '');
  const timestamp = data.timestamp
    || data.eventAt
    || data.visitDate
    || data.serviceDate
    || data.checkDate
    || data.endedAt
    || data.lastSeen
    || data.receivedAt
    || data.createdAt
    || data.updatedAt
    || null;
  const automaticRecord = automatic
    || data.automatic === true
    || data.isAutoMelding === true
    || source === 'robotEvents';
  const normalized = {
    ...data,
    id: docSnap.id,
    source: data.source || source,
    sourceCollection: source,
    path: docSnap.ref?.path || `${source}/${docSnap.id}`,
    branchId: data.branchId || data.vestigingId || branchId,
    branchIdentityConflict: [data.branchId, data.vestigingId]
      .map(textValue)
      .filter(Boolean)
      .some((declaredBranchId) => declaredBranchId !== textValue(branchId)),
    robotDocumentId: robotDocumentId || undefined,
    robotDocId: robotDocumentId || undefined,
    robotId: robotId || undefined,
    robotSerial: effectiveSerial || undefined,
    serialNumber: effectiveSerial || undefined,
    documentIdAliases,
    robotIdAliases,
    serialAliases,
    automatic: automaticRecord,
    isAutoMelding: automaticRecord || data.isAutoMelding === true,
    meldingType: automaticRecord ? 'automatisch' : data.meldingType,
  };
  if (!normalized.timestamp && timestamp) normalized.timestamp = timestamp;

  if (source === 'partsUsageLog') {
    const quantity = data.quantity ?? data.aantal ?? data.count;
    if (normalized.partsCount == null && quantity != null) normalized.partsCount = quantity;
  }
  if (source === 'reistijden' && normalized.travelHours == null) {
    normalized.travelHours = data.reistijdHours ?? data.reistijd ?? data.travelTime ?? null;
  }
  if (source === 'maintenance' && normalized.maintenanceHours == null) {
    normalized.maintenanceHours = data.onderhoudsuren ?? data.workedHours ?? data.uren ?? null;
  }
  return normalized;
}

async function readOptionalAnalyticsSource({
  source,
  diagnosticKey = source,
  reader,
  branchId,
  diagnostics,
  automatic = false,
  identityHint = '',
}) {
  try {
    const snapshot = await reader();
    const records = snapshot.docs.map((docSnap) => normalizeAnalyticsSourceRecord(docSnap, {
      source,
      branchId,
      automatic,
      identityHint,
    }));
    diagnostics.sources[diagnosticKey] = {
      status: 'ready',
      rawRecords: records.length,
    };
    return records;
  } catch (error) {
    diagnostics.sources[diagnosticKey] = {
      status: 'error',
      rawRecords: 0,
      error: error?.message || String(error),
    };
    diagnostics.readErrors.push({ source: diagnosticKey, error: error?.message || String(error) });
    return [];
  }
}

export async function loadAnalyticsData({ db, branchId, resolveVestigingIdWithData }) {
  if (!branchId) return {
    meldingen: [], performanceRecords: [], robots: [], sourceRecords: 0, automaticSourceRecords: 0, manualReview: [], automaticReview: [], performanceReview: [],
    performanceNorms: [], status: 'idle',
  };
  const resolvedBranchId = (await resolveVestigingIdWithData(branchId, 'meldingen')) || branchId;
  const [meldingenSnapshot, robotsSnapshot, autoMeldingenSnapshot, groupsSnapshot] = await Promise.all([
    getDocs(collection(db, ...getAnalyticsCollectionPath(resolvedBranchId))),
    getDocs(collection(db, 'vestigingen', resolvedBranchId, 'robots')),
    getDocs(collection(db, 'vestigingen', resolvedBranchId, 'autoMeldingen')),
    getDocs(query(collection(db, 'vestigingen', resolvedBranchId, 'groups'), limit(200))).catch(() => null),
  ]);
  const meldingen = meldingenSnapshot.docs.map(docSnap => ({ id: docSnap.id, source: 'vestiging', sourceCollection: 'meldingen', path: docSnap.ref.path, ...docSnap.data() }));
  const autoMeldingen = autoMeldingenSnapshot.docs.map(docSnap => ({ id: docSnap.id, source: 'vestiging_auto', sourceCollection: 'autoMeldingen', path: docSnap.ref.path, ...docSnap.data() }));
  const robots = robotsSnapshot.docs.map(docSnap => ({ ...docSnap.data(), docId: docSnap.id }));
  const groups = groupsSnapshot?.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id })).filter((group) => group.archived !== true) || [];
  const sourceDiagnostics = { sources: {}, readErrors: [] };
  sourceDiagnostics.sources.manualMeldingen = { status: 'ready', rawRecords: meldingen.length };
  sourceDiagnostics.sources.automaticMeldingen = { status: 'ready', rawRecords: autoMeldingen.length };

  const branchSourceNames = [
    'robot_dossiers',
    'checklists',
    'partsUsageLog',
    'reistijden',
    'maintenance',
    'garanties',
    'robotHealthSummary',
    'myRobotLiveStatus',
  ];
  const branchSourceRecords = await Promise.all(branchSourceNames.map((source) => readOptionalAnalyticsSource({
    source,
    branchId: resolvedBranchId,
    diagnostics: sourceDiagnostics,
    reader: () => getDocs(collection(db, 'vestigingen', resolvedBranchId, source)),
    automatic: false,
  })));
  const cycleSerials = [...new Set(robots.map((robot) => firstTextValue([
    robot.robotSerial,
    robot.serial,
    robot.serialNumber,
    robot.belroboticsId,
  ])).filter(Boolean))];
  const cycleHealthReads = await Promise.all(cycleSerials.map((serial) => readOptionalAnalyticsSource({
    source: 'cycleHealth',
    diagnosticKey: `cycleHealth:${serial}`,
    branchId: resolvedBranchId,
    diagnostics: sourceDiagnostics,
    identityHint: serial,
    reader: () => getDocs(query(
      collection(db, 'vestigingen', resolvedBranchId, 'robots', serial, 'cycleHealth'),
      limit(120),
    )),
  })));
  const cycleHealthRecords = cycleHealthReads.flat();
  sourceDiagnostics.sources.cycleHealth = {
    status: cycleHealthReads.some((records) => records.length > 0) ? 'ready' : 'empty',
    rawRecords: cycleHealthRecords.length,
  };

  const sourceGroups = [
    { key: 'manualMeldingen', records: meldingen, includeAutomatic: false, recordType: 'manual_visit' },
    { key: 'automaticMeldingen', records: autoMeldingen, includeAutomatic: true, recordType: 'automatic_incident' },
    ...branchSourceNames.map((key, index) => ({ key, records: branchSourceRecords[index], includeAutomatic: false, recordType: key })),
    { key: 'cycleHealth', records: cycleHealthRecords, includeAutomatic: false, recordType: 'cycle_health' },
  ];
  const preparedSources = sourceGroups.map((sourceGroup) => ({
    ...sourceGroup,
    prepared: prepareAnalyticsMeldingen(sourceGroup.records, robots, {
      includeAutomatic: sourceGroup.includeAutomatic,
      recordType: sourceGroup.recordType,
    }),
  }));
  const prepared = preparedSources.find((sourceGroup) => sourceGroup.key === 'manualMeldingen').prepared;
  const automaticPrepared = preparedSources.find((sourceGroup) => sourceGroup.key === 'automaticMeldingen').prepared;
  const linkedRecords = [...prepared.linked, ...automaticPrepared.linked];
  const performanceRecords = preparedSources.flatMap((sourceGroup) => sourceGroup.prepared.linked);
  const performanceReview = preparedSources.flatMap((sourceGroup) => sourceGroup.prepared.manualReview.map((review) => ({
    ...review,
    source: sourceGroup.key,
  })));
  const autoCount = autoMeldingen.length;

  // compute centralized analytics counts
  const manualDocs = prepared.linked.concat(prepared.manualReview.map(m => m.melding));
  const linkedIds = prepared.linked.map(d => d.id || d._id || '');
  const manualReviewIds = prepared.manualReview.map(m => m.meldingId || m.melding?.id || '');
  const counts = analyticsService.computeAnalyticsCounts({ manualDocs, autoMeldingenCount: autoCount, linkedIds, manualReviewIds, robots });
  counts.activeRobots = counts.activeRobotsCount;
  counts.robotsWithHistoricalResults = linkedRecords.reduce((set, record) => {
    const key = record.robotDocumentId || record.kNummer || record.robotId || record.robot || '';
    if (key) set.add(String(key));
    return set;
  }, new Set()).size;
  counts.lastUpdatedAt = new Date().toISOString();

  const performanceSourceRecords = sourceGroups.reduce((total, sourceGroup) => total + sourceGroup.records.length, 0);
  const performanceLinkedRecords = performanceRecords.length;

  return {
    meldingen: linkedRecords,
    performanceRecords,
    robots,
    groups,
    groupsStatus: groupsSnapshot ? 'ready' : 'error',
    groupsError: groupsSnapshot ? null : 'Groepen konden niet worden geladen.',
    sourceRecords: prepared.sourceRecords,
    automaticSourceRecords: automaticPrepared.sourceRecords,
    manualReview: prepared.manualReview,
    automaticReview: automaticPrepared.manualReview,
    performanceReview,
    performanceNorms: [],
    status: 'ready',
    resolvedBranchId,
    diagnostics: {
      branchId,
      resolvedBranchId,
      sourceRecords: prepared.sourceRecords,
      manualReview: prepared.manualReview.length,
      manualSourceRecords: prepared.sourceRecords,
      manualLinkedRecords: prepared.linked.length,
      manualReviewRecords: prepared.manualReview.length,
      automaticSourceRecords: automaticPrepared.sourceRecords,
      automaticLinkedRecords: automaticPrepared.linked.length,
      automaticReviewRecords: automaticPrepared.manualReview.length,
      performanceSourceRecords,
      performanceLinkedRecords,
      performanceReviewRecords: performanceReview.length,
      sources: preparedSources.reduce((result, sourceGroup) => {
        const preparedSource = sourceGroup.prepared;
        result[sourceGroup.key] = {
          ...(sourceDiagnostics.sources[sourceGroup.key] || {}),
          linkedRecords: preparedSource.linked.length,
          rejectedRecords: preparedSource.manualReview.length,
        };
        return result;
      }, {}),
      readErrors: sourceDiagnostics.readErrors,
    },
    analyticsCounts: counts,
    countContract: buildAnalyticsCountContract({
      sourceRecords: prepared.sourceRecords,
      visibleRecords: prepared.linked.length,
      manualReview: prepared.manualReview.length,
      activeRobots: counts.activeRobotsCount,
      robotsWithHistoricalResults: counts.robotsWithHistoricalResults,
      updatedAt: counts.lastUpdatedAt,
    }),
  };
}

export async function loadAnalyticsSelectorData({ db, branchId, resolveVestigingIdWithData }) {
  if (!branchId) return { robots: [], status: 'idle' };
  const resolvedBranchId = (await resolveVestigingIdWithData(branchId, 'robots')) || branchId;
  const [robotsSnapshot, groupsResult] = await Promise.all([
    getDocs(collection(db, 'vestigingen', resolvedBranchId, 'robots')),
    getDocs(query(collection(db, 'vestigingen', resolvedBranchId, 'groups'), limit(200)))
      .then((snapshot) => ({ groups: snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id })).filter((group) => group.archived !== true), status: 'ready' }))
      .catch((error) => ({ groups: [], status: 'error', error })),
  ]);
  return {
    robots: robotsSnapshot.docs.map((docSnap) => ({ ...docSnap.data(), docId: docSnap.id })),
    groups: groupsResult.groups,
    groupsStatus: groupsResult.status,
    groupsError: groupsResult.error?.message || null,
    status: 'ready',
    resolvedBranchId,
    diagnostics: { resolvedBranchId, selectorRecords: robotsSnapshot.docs.length, groups: groupsResult.groups.length, groupsStatus: groupsResult.status },
  };
}

const RPD_SOURCE_FIELDS = {
  meldingen: ['robotDocumentId', 'robotDocId', 'robotId', 'robotID', 'sourceRobotId', 'kNummer', 'robot', 'serialNumber', 'robotSerial', 'serial', 'belroboticsId', 'belroboticsID', 'selectedRobot.id', 'selectedRobot.robot.id', 'selectedRobot.robot.serialNumber'],
  autoMeldingen: ['robotDocumentId', 'robotDocId', 'robotId', 'robotID', 'sourceRobotId', 'kNummer', 'robot', 'serialNumber', 'robotSerial', 'serial', 'belroboticsId', 'belroboticsID', 'selectedRobot.id', 'selectedRobot.robot.id', 'selectedRobot.robot.serialNumber'],
  robot_dossiers: ['robotDocumentId', 'robotDocId', 'robotId', 'robotID', 'sourceRobotId', 'kNummer', 'robot', 'serialNumber', 'robotSerial', 'serial', 'belroboticsId', 'belroboticsID', 'selectedRobot.id', 'selectedRobot.robot.id', 'selectedRobot.robot.serialNumber'],
  checklists: ['robotDocumentId', 'robotDocId', 'robotId', 'robotID', 'sourceRobotId', 'kNummer', 'robot', 'serialNumber', 'robotSerial', 'serial', 'belroboticsId', 'belroboticsID', 'selectedRobot.id', 'selectedRobot.robot.id', 'selectedRobot.robot.serialNumber'],
  partsUsageLog: ['robotDocumentId', 'robotDocId', 'robotId', 'robotID', 'sourceRobotId', 'kNummer', 'robot', 'serialNumber', 'robotSerial', 'serial', 'belroboticsId', 'belroboticsID', 'selectedRobot.id', 'selectedRobot.robot.id', 'selectedRobot.robot.serialNumber'],
  reistijden: ['robotDocumentId', 'robotDocId', 'robotId', 'robotID', 'sourceRobotId', 'kNummer', 'robot', 'serialNumber', 'robotSerial', 'serial', 'belroboticsId', 'belroboticsID', 'selectedRobot.id', 'selectedRobot.robot.id', 'selectedRobot.robot.serialNumber'],
  maintenance: ['robotDocumentId', 'robotDocId', 'robotId', 'robotID', 'sourceRobotId', 'kNummer', 'robot', 'serialNumber', 'robotSerial', 'serial', 'belroboticsId', 'belroboticsID', 'selectedRobot.id', 'selectedRobot.robot.id', 'selectedRobot.robot.serialNumber'],
  garanties: ['robotDocumentId', 'robotDocId', 'robotId', 'robotID', 'sourceRobotId', 'kNummer', 'robot', 'serialNumber', 'robotSerial', 'serial', 'belroboticsId', 'belroboticsID', 'selectedRobot.id', 'selectedRobot.robot.id', 'selectedRobot.robot.serialNumber'],
  robotHealthSummary: ['robotDocumentId', 'robotDocId', 'robotId', 'robotID', 'sourceRobotId', 'kNummer', 'robot', 'serialNumber', 'robotSerial', 'serial', 'belroboticsId', 'belroboticsID', 'selectedRobot.id', 'selectedRobot.robot.id', 'selectedRobot.robot.serialNumber'],
  myRobotLiveStatus: ['robotDocumentId', 'robotDocId', 'robotId', 'robotID', 'sourceRobotId', 'kNummer', 'robot', 'serialNumber', 'robotSerial', 'serial', 'belroboticsId', 'belroboticsID', 'selectedRobot.id', 'selectedRobot.robot.id', 'selectedRobot.robot.serialNumber'],
};

function selectedRobotValues(robot = {}) {
  const resolution = resolveRobotRecord(robot, [robot]);
  if (resolution.status !== IDENTITY_STATUS.PROVEN) return [];
  return uniqueTextValues([
    resolution.robot.documentId,
    resolution.robot.robotId,
    resolution.robot.serialNumber,
  ]);
}

async function readSelectedAnalyticsSource({ db, branchId, source, robot, diagnostics, pageSize = 25, pageCursors = {} }) {
  const values = selectedRobotValues(robot);
  if (!values.length) return { records: [], nextCursors: {} };
  const sourceCollection = collection(db, 'vestigingen', branchId, source);
  const fields = RPD_SOURCE_FIELDS[source] || [];
  const fieldErrors = [];
  const valueChunks = Array.from({ length: Math.ceil(values.length / 10) }, (_, index) => values.slice(index * 10, (index + 1) * 10));
  const queries = fields.flatMap((field) => valueChunks.map((valueChunk, chunkIndex) => ({
    field,
    chunkIndex,
    cursorKey: chunkIndex === 0 ? field : `${field}::${chunkIndex}`,
    valueChunk,
  })));
  const snapshots = await Promise.all(queries.map(async ({ field, chunkIndex, cursorKey, valueChunk }) => {
    try {
      const cursor = pageCursors[cursorKey] || (chunkIndex === 0 ? pageCursors[field] : null);
      return await getDocs(query(
        sourceCollection,
        where(field, 'in', valueChunk),
        ...(cursor ? [startAfter(cursor)] : []),
        limit(pageSize),
      ));
    } catch (error) {
      const message = error?.message || String(error);
      fieldErrors.push({ field, chunkIndex, message });
      diagnostics.readErrors.push({ source, field, chunkIndex, error: message });
      return { docs: [], readError: true };
    }
  }));
  const unique = new Map();
  const branchIdentityConflicts = new Set();
  snapshots.flatMap((snapshot) => snapshot.docs || []).forEach((docSnap) => {
    const normalized = normalizeAnalyticsSourceRecord(docSnap, {
      source,
      branchId,
      automatic: source === 'autoMeldingen',
    });
    if (normalized.branchIdentityConflict) {
      branchIdentityConflicts.add(docSnap.id);
      return;
    }
    unique.set(docSnap.id, normalized);
  });
  diagnostics.sources[source] = {
    status: fieldErrors.length > 0 ? 'error' : branchIdentityConflicts.size > 0 ? 'partial' : 'ready',
    rawRecords: unique.size + branchIdentityConflicts.size,
    rejectedBranchIdentityRecords: branchIdentityConflicts.size,
    rejectedRecordKeys: [...branchIdentityConflicts].map((id) => `${source}:${id}:branch-identity`),
    queryFields: fields,
    failedQueryFields: fieldErrors.map(({ field }) => field),
  };
  return {
    records: [...unique.values()],
    nextCursors: queries.reduce((result, { cursorKey }, index) => {
      const docs = snapshots[index]?.docs || [];
      if (docs.length >= pageSize) result[cursorKey] = docs[docs.length - 1];
      return result;
    }, {}),
  };
}

export async function loadRobotPerformanceData({ db, branchId, robot, resolveVestigingIdWithData, pageSize = 25, pageCursors = {}, periodFrom = null, periodTo = null }) {
  if (!branchId || !robot) return { records: [], status: 'idle', diagnostics: { sources: {} } };
  const resolvedBranchId = (await resolveVestigingIdWithData(branchId, 'robots')) || branchId;
  const diagnostics = { sources: {}, readErrors: [], selectedRobotId: robot.docId || robot.id || null };
  const sourceNames = [...new Set(['meldingen', 'autoMeldingen', ...Object.keys(RPD_SOURCE_FIELDS)])];
  const sourceRecords = await Promise.all(sourceNames.map((source) => readSelectedAnalyticsSource({
    db,
    branchId: resolvedBranchId,
    source,
    robot,
    diagnostics,
    pageSize,
    pageCursors: pageCursors[source] || {},
  })));
  if (diagnostics.readErrors.length > 0) {
    const failedSources = [...new Set(diagnostics.readErrors.map(({ source }) => source))].join(', ');
    throw new Error(`RPD-brongegevens konden niet volledig worden geladen (${failedSources}).`);
  }
  const robots = [robot];
  const records = sourceRecords.flatMap((sourceResult, index) => {
    const source = sourceNames[index];
    const preparedResult = prepareAnalyticsMeldingen(sourceResult.records, robots, {
      includeAutomatic: source === 'autoMeldingen',
      recordType: source === 'autoMeldingen' ? 'automatic_incident' : source === 'meldingen' ? 'manual_visit' : source,
      strictStableIdentity: true,
    });
    const prepared = preparedResult.linked;
    const rejectedRecordKeys = [
      ...(diagnostics.sources[source]?.rejectedRecordKeys || []),
      ...preparedResult.manualReview.map(({ meldingId, melding }) => `${source}:${meldingId || melding?.id || melding?._id || ''}`).filter((key) => !key.endsWith(':')),
    ];
    const uniqueRejectedRecordKeys = [...new Set(rejectedRecordKeys)];
    diagnostics.sources[source] = {
      ...diagnostics.sources[source],
      linkedRecords: prepared.length,
      rejectedRecords: uniqueRejectedRecordKeys.length,
      rejectedRecordKeys: uniqueRejectedRecordKeys,
    };
    return prepared;
  }).filter((record) => {
    const value = record.timestamp;
    const milliseconds = typeof value?.toMillis === 'function' ? value.toMillis() : typeof value?.toDate === 'function' ? value.toDate().getTime() : Date.parse(String(value || ''));
    if (Number.isFinite(periodFrom) && (!Number.isFinite(milliseconds) || milliseconds < periodFrom)) return false;
    if (Number.isFinite(periodTo) && (!Number.isFinite(milliseconds) || milliseconds >= periodTo)) return false;
    return true;
  });
  const serials = selectedRobotValues(robot).filter((value) => /^BR[A-Z0-9]{6,}$/i.test(value));
  const cycleHealth = (await Promise.all(serials.map((serial) => readOptionalAnalyticsSource({
    source: 'cycleHealth',
    diagnosticKey: `cycleHealth:${serial}`,
    branchId: resolvedBranchId,
    diagnostics,
    identityHint: serial,
    reader: () => getDocs(query(collection(db, 'vestigingen', resolvedBranchId, 'robots', serial, 'cycleHealth'), limit(25))),
  })))).flat();
  return {
    records: [...records, ...cycleHealth].filter((record) => {
      const value = record.timestamp;
      const milliseconds = typeof value?.toMillis === 'function' ? value.toMillis() : typeof value?.toDate === 'function' ? value.toDate().getTime() : Date.parse(String(value || ''));
      return (!Number.isFinite(periodFrom) || (Number.isFinite(milliseconds) && milliseconds >= periodFrom))
        && (!Number.isFinite(periodTo) || (Number.isFinite(milliseconds) && milliseconds < periodTo));
    }),
    nextCursors: sourceRecords.reduce((result, sourceResult, index) => {
      if (Object.keys(sourceResult.nextCursors).length > 0) result[sourceNames[index]] = sourceResult.nextCursors;
      return result;
    }, {}),
    status: 'ready',
    resolvedBranchId,
    diagnostics: { ...diagnostics, fetchedRecords: records.length + cycleHealth.length },
  };
}

export async function loadMeldingenData({
  db,
  userBranchId,
  isSuperAdmin,
  logger,
  user,
  resolveVestigingIdWithData,
  canonicalizeVestigingId,
  getVestigingIdCandidates,
}) {
  try {
    logger.debug('MELDINGEN_LOAD', `Starting load - userBranchId: ${userBranchId}, isSuperAdmin: ${isSuperAdmin}`);

    if (!userBranchId && !isSuperAdmin) {
      logger.warn('MELDINGEN_LOAD', '⚠️ No userBranchId and not super admin - cannot load data');
      return { meldingenData: [], meldingenBadgeCount: 0 };
    }

    let meldingenData = [];
    const dedupeMap = new Map();
    const heerlenAliases = new Set(['y6yv2avw6ujwg7dbfm2q', 'heerlen', 'krinkels-heerlen', 'krinkels heerlen']);

    const normalizeBranchToken = (value) => {
      const raw = String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
      if (!raw) return '';
      if (heerlenAliases.has(raw)) return 'krinkels heerlen';
      return raw;
    };

    const getMeldingUniqueKey = (melding = {}) => {
      const branchToken = normalizeBranchToken(
        melding.vestigingId || melding.vestiging || melding.branchId || melding.branchName || ''
      );
      const idToken = String(melding.id || '').trim();
      if (idToken) return `${branchToken}|${idToken}`;

      const tsToken = String(melding.timestamp?.seconds || melding.timestamp || '').trim();
      const nameToken = String(melding.naam || melding.robot || '').trim().toLowerCase();
      const projToken = String(melding.projectNummer || melding.projectCode || '').trim().toLowerCase();
      return `${branchToken}|${tsToken}|${nameToken}|${projToken}`;
    };

    const normalizeLoadedMelding = (melding = {}) => {
      const canonicalEvent = normalizeCanonicalEvent(melding, { branchId: userBranchId });
      const isAutomatic = canonicalEvent.isAutomatic;
      if (!isAutomatic) {
        return {
          ...melding,
          canonicalEvent,
          meldingType: melding.meldingType || 'handmatig',
          isAutoMelding: false,
          isManualMelding: true,
          meldingEntryType: melding.meldingEntryType || 'Manual',
        };
      }
      return {
        ...melding,
        canonicalEvent,
        isAutoMelding: true,
        isManualMelding: false,
        meldingType: 'automatisch',
        meldingEntryType: 'Automatic',
        source: melding.source || 'email_intake',
        author: melding.author || 'Automatisch',
        username: melding.username || 'Automatisch',
        technician: melding.technician || 'Automatisch',
        technicianName: melding.technicianName || 'Automatisch',
      };
    };

    const upsertUnique = (melding) => {
      if (!melding) return;
      const key = getMeldingUniqueKey(melding);
      const current = dedupeMap.get(key);
      if (!current) {
        dedupeMap.set(key, melding);
        return;
      }

      const currentRank = (current.source === 'vestiging' || current.source === 'vestiging_auto') ? 2 : 1;
      const nextRank = (melding.source === 'vestiging' || melding.source === 'vestiging_auto') ? 2 : 1;
      if (nextRank >= currentRank) {
        dedupeMap.set(key, melding);
      }
    };

    if (isSuperAdmin) {
      logger.logSuperAdminAccess('Meldingen', 1);

      try {
        const rootSnap = await getDocs(collection(db, 'meldingen'));
        rootSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data && data.placeholder) return;
          upsertUnique({
            id: docSnap.id,
            source: 'root',
            path: docSnap.ref.path,
            vestiging: data?.branchId || data?.vestigingId || data?.vestiging || null,
            vestigingId: data?.branchId || data?.vestigingId || data?.vestiging || null,
            ...data,
          });
        });
        logger.info('MELDINGEN_LOAD', `⭐ SUPER ADMIN: Loaded ${rootSnap.docs.length} root meldingen`);
      } catch (error) {
        logger.warn('MELDINGEN_LOAD', 'Root meldingen load failed (non-fatal)', error?.message || error);
      }

      const branchesSnap = await getDocs(collection(db, 'branches'));
      logger.info('MELDINGEN_LOAD', `⭐ Found ${branchesSnap.docs.length} branches to load meldingen from`);

      const branchResults = await mapWithConcurrency(branchesSnap.docs, 5, async (branchDoc) => {
        const branchData = branchDoc.data() || {};
        const branchId = branchData.name || branchDoc.id;
        logger.debug('MELDINGEN_LOAD', `Loading meldingen from branch: ${branchId}`);
        try {
          const [subSnap, autoSnap] = await Promise.all([
            getDocs(collection(db, 'vestigingen', branchId, 'meldingen')),
            getDocs(collection(db, 'vestigingen', branchId, 'autoMeldingen')),
          ]);
          return { branchId, subSnap, autoSnap };
        } catch (error) {
          logger.warn('MELDINGEN_LOAD', `Failed to load meldingen for branch ${branchId}`, error?.message || error);
          return null;
        }
      });

      for (const branchResult of branchResults) {
        if (!branchResult) continue;
        const { branchId, subSnap, autoSnap } = branchResult;
        subSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data && data.placeholder) return;
          upsertUnique(normalizeLoadedMelding({
            id: docSnap.id,
            source: 'vestiging',
            path: docSnap.ref.path,
            vestiging: branchId,
            vestigingId: branchId,
            ...data,
          }));
        });
        autoSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data && data.placeholder) return;
          upsertUnique(normalizeLoadedMelding({
            id: docSnap.id,
            source: 'vestiging_auto',
            path: docSnap.ref.path,
            vestiging: branchId,
            vestigingId: branchId,
            ...data,
          }));
        });
      }
    } else {
      if (!userBranchId) {
        logger.error('MELDINGEN_LOAD', '❌ Regular user has no branchId!');
        return { meldingenData: [], meldingenBadgeCount: 0 };
      }

      const resolvedVestigingId = (await resolveVestigingIdWithData(userBranchId, 'meldingen')) || userBranchId;
      const vestigingCandidates = Array.from(new Set([
        resolvedVestigingId,
        userBranchId,
        canonicalizeVestigingId(userBranchId),
        ...getVestigingIdCandidates(userBranchId),
      ].filter(Boolean)));

      logger.info('MELDINGEN_LOAD', '👤 Regular user loading meldingen from candidates', {
        userBranchId,
        resolvedVestigingId,
        vestigingCandidates,
      });
      logger.info('CHECK_USER_DATA', `Gebruiker: ${user?.email || user?.uid || 'onbekend'}`);
      logger.info('CHECK_USER_DATA', `Ingestelde vestiging: ${userBranchId}`);

      try {
        const readJobs = vestigingCandidates.flatMap((vestigingId) => [
          { source: 'vestiging', vestigingId, collectionName: 'meldingen' },
          { source: 'vestiging_auto', vestigingId, collectionName: 'autoMeldingen' },
          { source: 'root', vestigingId },
        ]);
        const readResults = await mapWithConcurrency(readJobs, 6, async (job) => {
          try {
            if (job.source === 'root') {
              const scopedQuery = query(collection(db, 'meldingen'), where('branchId', '==', job.vestigingId));
              return { ...job, snapshot: await getDocs(scopedQuery) };
            }
            logger.logCollectionPath(`vestigingen/${job.vestigingId}/${job.collectionName}`, 0);
            return {
              ...job,
              snapshot: await getDocs(collection(db, 'vestigingen', job.vestigingId, job.collectionName)),
            };
          } catch {
            return { ...job, snapshot: null };
          }
        });

        for (const readResult of readResults) {
          if (!readResult.snapshot) continue;
          const { source, vestigingId, snapshot } = readResult;
          snapshot.docs.forEach((docSnap) => {
            const data = docSnap.data();
            if (data && data.placeholder) return;
            const melding = {
              id: docSnap.id,
              source,
              path: docSnap.ref.path,
              vestiging: vestigingId,
              vestigingId,
              ...data,
            };
            upsertUnique(source === 'root' ? melding : normalizeLoadedMelding(melding));
          });
        }

        logger.logDataQuery(resolvedVestigingId, 'meldingen', meldingenData.length, false);
        if (meldingenData.length > 0) {
          logger.info('CHECK_USER_DATA', `✅ Gebruiker ziet data van vestiging-candidates: ${vestigingCandidates.join(', ')}`);
        } else {
          logger.warn('CHECK_USER_DATA', `❌ Gebruiker ziet GEEN data van vestiging-candidates: ${vestigingCandidates.join(', ')}`);
        }
      } catch (queryError) {
        logger.error('MELDINGEN_LOAD', `❌ Query failed for vestiging ${userBranchId}:`, queryError);
        throw queryError;
      }
    }

    meldingenData = Array.from(dedupeMap.values());
    meldingenData = sortRecordsNewestFirst(meldingenData);

    return { meldingenData, meldingenBadgeCount: countUnreadHumanMeldingen(meldingenData, userBranchId) };
  } catch (error) {
    logger.error('MELDINGEN_LOAD', 'Error loading meldingen:', error);
    return { meldingenData: [], meldingenBadgeCount: 0 };
  }
}

export async function loadOnderdelenData({ db, userBranchId, resolveVestigingIdWithData }) {
  try {
    const data = await import('../../assets/onderdelen.json');
    const jsonOnderdelen = data.default || [];

    if (!userBranchId) {
      return jsonOnderdelen;
    }

    const resolvedVestigingId = (await resolveVestigingIdWithData(userBranchId, 'onderdelen')) || userBranchId;
    const { getMagazijnVoorraad } = await import('../../utils/magazijn');
    const voorraadData = await getMagazijnVoorraad(userBranchId);

    const metadataSnapshot = await getDocs(collection(db, 'vestigingen', resolvedVestigingId, 'onderdeel_metadata'));
    const metadataMap = {};
    metadataSnapshot.docs.forEach((docSnap) => {
      metadataMap[docSnap.id] = docSnap.data();
    });

    const deletedCodes = new Set(
      Object.entries(metadataMap)
        .filter(([, meta]) => meta?.deleted === true)
        .map(([code]) => code)
    );

    const onderdelenMetVoorraad = voorraadData.filter((item) => (item.aantal ?? 0) > 0 && !deletedCodes.has(item.id));

    const combinedOnderdelen = onderdelenMetVoorraad.map((item) => {
      const jsonOnderdeel = jsonOnderdelen.find((entry) => entry.code === item.id);
      const metadata = metadataMap[item.id];
      return {
        code: item.id,
        name: metadata?.name || jsonOnderdeel?.name || item.id,
        aantal: item.aantal,
        minimumStock: normalizeMinimumStock(metadata?.minimumStock, 1),
      };
    });

    const missingOnderdelen = jsonOnderdelen
      .filter((item) => !onderdelenMetVoorraad.some((voorraad) => voorraad.id === item.code) && !deletedCodes.has(item.code))
      .map((item) => ({
        code: item.code,
        name: item.name,
        aantal: 0,
        minimumStock: normalizeMinimumStock(metadataMap[item.code]?.minimumStock, 1),
      }));

    return [...combinedOnderdelen, ...missingOnderdelen];
  } catch (err) { void err; const data = await import('../../assets/onderdelen.json'); return data.default || []; }
}

export async function loadMagazijnVoorraadData(userBranchId) {
  try {
    if (!userBranchId) return [];
    const { getMagazijnVoorraad } = await import('../../utils/magazijn');
    return await getMagazijnVoorraad(userBranchId);
  } catch (err) { void err; return []; }
}

export async function loadBijzonderhedenData({ db, userBranchId, resolveVestigingIdWithData }) {
  try {
    if (!userBranchId) return [];

    const resolvedVestigingId = (await resolveVestigingIdWithData(userBranchId, 'robot_bijzonderheden'))
      || (await resolveVestigingIdWithData(userBranchId, 'bijzonderheden'))
      || userBranchId;

    const all = [];
    const seen = new Set();
    const pushUnique = (docSnap, source) => {
      if (!docSnap) return;
      const key = `${source}:${docSnap.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      all.push({ id: docSnap.id, source, path: docSnap.ref?.path || null, ...docSnap.data() });
    };

    try {
      const snap = await getDocs(collection(db, 'vestigingen', resolvedVestigingId, 'robot_bijzonderheden'));
      snap.docs.forEach((docSnap) => pushUnique(docSnap, 'robot_bijzonderheden'));
            } catch (err) { void err; }

    try {
      const snap = await getDocs(collection(db, 'vestigingen', resolvedVestigingId, 'bijzonderheden'));
      snap.docs.forEach((docSnap) => pushUnique(docSnap, 'bijzonderheden'));
    } catch (err) { void err; }

    return all.filter((item) => !(item && item.placeholder));
  } catch (error) {
    console.error('Error loading bijzonderheden:', error);
    return [];
  }
}

export async function loadGarantiesData({ db, userBranchId, resolveVestigingIdWithData }) {
  try {
    if (!userBranchId) return [];

    const resolvedVestigingId = (await resolveVestigingIdWithData(userBranchId, 'garanties')) || userBranchId;
    const all = [];
    const seen = new Set();
    const pushUnique = (docSnap, source) => {
      if (!docSnap) return;
      const key = `${source}:${docSnap.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      all.push({ id: docSnap.id, source, path: docSnap.ref?.path || null, ...docSnap.data() });
    };

    try {
      const snap = await getDocs(collection(db, 'vestigingen', resolvedVestigingId, 'garanties'));
      snap.docs.forEach((docSnap) => pushUnique(docSnap, 'vestiging'));
    } catch (err) { void err; }

    return all.filter((item) => !(item && item.placeholder) && item.active !== false);
  } catch (error) {
    console.error('Error loading garanties:', error);
    return [];
  }
}
